import { LoadingOverlay, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import useUpdateMaintenanceServiceMetadata from '../../../hooks/maintenance-services/useUpdateMaintenanceServiceMetadata.ts';
import {
  UpdateMaintenanceServiceMetadataRequest,
  UpdateMaintenanceServiceOperationHoursRequest,
  UpdateMaintenanceServicePaymentMethodsRequest,
  UpdateMaintenanceServiceTagsRequest,
} from '../../../api/requests/maintenance-service-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import useMaintenanceService from '../../../hooks/maintenance-services/useMaintenanceService.ts';
import useUpdateMaintenanceServiceOperationHours from '../../../hooks/maintenance-services/useUpdateMaintenanceServiceOperationHours.ts';
import { notifySuccess } from '../../../utils/NotifySuccess.tsx';
import useEnableMaintenanceService from '../../../hooks/maintenance-services/useEnableMaintenanceService.ts';
import useDisableMaintenanceService from '../../../hooks/maintenance-services/useDisableMaintenanceService.ts';
import usePauseMaintenanceService from '../../../hooks/maintenance-services/usePauseMaintenanceService.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ErrorResponse } from '../../../api/responses/error-response.ts';
import MutateUpdateMaintenanceServiceForm, {
  MutateUpdateMaintenanceServiceFormValues,
} from '../../ui/places/MutateUpdateMaintenanceServiceForm.tsx';
import { PhoneNumberUtil } from 'google-libphonenumber';
import useUpdateMaintenanceServiceTags from '../../../hooks/maintenance-services/useUpdateMaintenanceServiceTags.ts';
import useUpdateMaintenanceServicePaymentMethods from '../../../hooks/maintenance-services/useUpdateMaintenanceServicePaymentMethods.ts';
import useUpdateMaintenanceServiceStoreIds from '../../../hooks/maintenance-services/useUpdateMaintenanceServiceStoreIds.ts';
import {
  buildStoreUrl,
  resolveStoreIdsChange,
} from '../../../utils/storeUrl.ts';

const phoneUtil = PhoneNumberUtil.getInstance();

interface UpdateMaintenanceServiceMetadataModalProps {
  placeId: string;
  maintenanceServiceId: string | null;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const UpdateMaintenanceServiceMetadataDrawer = ({
  placeId,
  maintenanceServiceId,
  onSuccess,
  onCancel,
}: UpdateMaintenanceServiceMetadataModalProps) => {
  const queryClient = useQueryClient();

  const { data: maintenanceService, isLoading: isMaintenanceServiceLoading } =
    useMaintenanceService(maintenanceServiceId);

  const { mutateAsync: updateMaintenanceServiceMetadata } =
    useUpdateMaintenanceServiceMetadata();
  const { mutateAsync: updateMaintenanceServiceOperationHours } =
    useUpdateMaintenanceServiceOperationHours();
  const { mutateAsync: updateMaintenanceServiceTags } =
    useUpdateMaintenanceServiceTags();
  const { mutateAsync: updateMaintenanceServicePaymentMethods } =
    useUpdateMaintenanceServicePaymentMethods();
  const { mutateAsync: updateMaintenanceServiceStoreIds } =
    useUpdateMaintenanceServiceStoreIds();
  const { mutateAsync: enableMaintenanceService } =
    useEnableMaintenanceService();
  const { mutateAsync: pauseMaintenanceService } = usePauseMaintenanceService();
  const { mutateAsync: disableMaintenanceService } =
    useDisableMaintenanceService();

  const {
    mutate: handleSubmitAll,
    isPending: isUpdateMaintenanceServicePending,
  } = useMutation<
    void,
    ErrorResponse,
    MutateUpdateMaintenanceServiceFormValues
  >({
    mutationFn: async (formValues) => {
      const storeIdsToSubmit = resolveStoreIdsChange(
        formValues.storeIdUrl,
        'MAINTENANCE',
        maintenanceService.storeIds,
      );
      if (storeIdsToSubmit !== null) {
        await updateMaintenanceServiceStoreIds({
          serviceId: maintenanceService.id,
          storeIds: storeIdsToSubmit,
        });
      }

      const parsedPhoneNumber =
        formValues.phoneNumberCountryCode !== '' &&
        formValues.phoneNumberValue !== ''
          ? phoneUtil.parse(
              `${formValues.phoneNumberCountryCode}${formValues.phoneNumberValue}`,
            )
          : null;

      const updateMaintenanceServiceMetadataRequest: UpdateMaintenanceServiceMetadataRequest =
        {
          serviceId: maintenanceService.id,
          name: formValues.name.trim(),
          brandId: formValues.brandId ?? '',
          phoneNumber: {
            countryCode: formValues.phoneNumberCountryCode ?? '',
            number: parsedPhoneNumber
              ? phoneUtil.getNationalSignificantNumber(parsedPhoneNumber)
              : '',
          },
          website: formValues.website,
          notes: formValues.notes,
          pricingSource: maintenanceService.pricingSource,
        };

      await updateMaintenanceServiceMetadata(
        updateMaintenanceServiceMetadataRequest,
      );

      if (formValues.serviceStatus === 'ONLINE') {
        await enableMaintenanceService(maintenanceService.id);
      } else if (formValues.serviceStatus === 'TEMPORARY_OFFLINE') {
        await pauseMaintenanceService(maintenanceService.id);
      } else {
        await disableMaintenanceService(maintenanceService.id);
      }

      const updateMaintenanceServiceTagsRequest: UpdateMaintenanceServiceTagsRequest =
        {
          serviceId: maintenanceService.id,
          tags: formValues.tags,
        };

      await updateMaintenanceServiceTags(updateMaintenanceServiceTagsRequest);

      const updateMaintenanceServicePaymentMethodsRequest: UpdateMaintenanceServicePaymentMethodsRequest =
        {
          serviceId: maintenanceService.id,
          paymentMethods: formValues.paymentMethods,
        };

      await updateMaintenanceServicePaymentMethods(
        updateMaintenanceServicePaymentMethodsRequest,
      );

      // 一律送出表單當下顯示的營業時間，確保 drawer 上看到的資料跟實際寫入的完全一致
      const updateMaintenanceServiceOperationHoursRequest: UpdateMaintenanceServiceOperationHoursRequest =
        {
          serviceId: maintenanceServiceId,
          operationHours: formValues.operationHours,
        };

      await updateMaintenanceServiceOperationHours(
        updateMaintenanceServiceOperationHoursRequest,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['maintenanceServices', maintenanceService.id],
        exact: true,
      });
      await queryClient.invalidateQueries({
        queryKey: ['places', placeId],
        exact: true,
      });
      notifySuccess('更新保修服務成功');
      onSuccess();
    },
    onError: (error) => {
      notifyError('更新保修服務失敗', error.message);
    },
  });

  const handleFormSubmit = (
    formValues: MutateUpdateMaintenanceServiceFormValues,
  ) => {
    let storeIdsToSubmit: ReturnType<typeof resolveStoreIdsChange>;
    try {
      storeIdsToSubmit = resolveStoreIdsChange(
        formValues.storeIdUrl,
        'MAINTENANCE',
        maintenanceService.storeIds,
      );
    } catch {
      return;
    }

    if (storeIdsToSubmit === null) {
      handleSubmitAll(formValues);
      return;
    }

    const isUnbinding = storeIdsToSubmit.length === 0;
    modals.openConfirmModal({
      title: (
        <Text style={{ fontSize: 16, fontWeight: 700 }}>
          {isUnbinding ? '解除綁定商店' : '綁定商店'}
        </Text>
      ),
      children: (
        <Text size="sm">
          {isUnbinding
            ? '解除綁定後，目前的營業時間不會被清空，會維持最後一次同步的資料，確定要繼續嗎？'
            : '綁定後將會用 autoplus 現有的營業時間覆蓋這個服務目前的資料，確定要繼續嗎？'}
        </Text>
      ),
      labels: { confirm: '是，我確定', cancel: '取消' },
      confirmProps: isUnbinding ? { color: 'red' } : undefined,
      onConfirm: () => handleSubmitAll(formValues),
    });
  };

  const handleClose = () => {
    onCancel();
  };

  if (isMaintenanceServiceLoading) return <LoadingOverlay />;

  return (
    <MutateUpdateMaintenanceServiceForm
      initialValues={{
        serviceId: maintenanceService.id,
        name: maintenanceService.name,
        serviceStatus: maintenanceService.serviceStatus,
        brandId: maintenanceService.brandId,
        storeIdUrl: maintenanceService.storeIds[0]
          ? buildStoreUrl(maintenanceService.storeIds[0])
          : '',
        operationHours: maintenanceService.operationHours,
        phoneNumberCountryCode: maintenanceService.phoneNumber?.countryCode,
        phoneNumberValue: maintenanceService.phoneNumber?.number,
        website: maintenanceService.website,
        notes: maintenanceService.notes,
        tags: maintenanceService.tags,
        paymentMethods: maintenanceService.paymentMethods,
      }}
      onSubmit={handleFormSubmit}
      onCancel={handleClose}
      isSubmitting={isUpdateMaintenanceServicePending}
      isUpdating={true}
    />
  );
};

export { UpdateMaintenanceServiceMetadataDrawer };
